import { ClosedOneWayCircle } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors } from '@creditornot/cb-ingredients';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { fetchVenueAvailabilityStatuses } from 'apis/restaurant-api/merchant-admin/venue/api';
import { Venue } from 'apis/restaurant-api/merchant-admin/venue/types';
import { ExperienceEntityType } from 'modules/experience-core';
import { useExperienceModuleContext } from 'modules/experience-core';

import { VenueAvailabilityStatus } from './types';
import { getVenueAvailabilityStatus } from './utils';

export const AvatarBackgroundContainer = styled.div`
  position: absolute;
  bottom: 1px;
  right: 1px;
  background-color: ${cbSemanticCssVars.surfaceMain};
  display: flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  width: 14px;
  border-radius: 50%;
`;

const InactiveIcon = styled(ClosedOneWayCircle).attrs({ width: 10, height: 10 })`
  fill: ${colors.strawberry100};
`;

const StatusLight = styled.div<{ status: VenueAvailabilityStatus }>`
  border-radius: 50%;
  height: 10px;
  width: 10px;
  opacity: 1;
  background-color: ${({ theme, status }) => {
    switch (status) {
      case VenueAvailabilityStatus.IPAD_NOT_ALIVE:
      case VenueAvailabilityStatus.OFFLINE:
        return colors.strawberry100;
      case VenueAvailabilityStatus.CLOSED:
        return theme.cbThemeMode === 'light' ? colors.pepper32 : colors.pepper32;
      default:
        return colors.lime100;
    }
  }};
`;

const REFETCH_VENUE_AVAILABILITY_STATUS_INTERVAL_MS = 5 * 60 * 1000; // 5 minute

const VenueAvailability = ({ venue }: { venue: Venue }) => {
  const { locale } = useIntl();
  const { data: venuesData } = useQuery(
    ['/v2/merchant-admin/venue_availability_statuses', venue.id, locale],
    () => fetchVenueAvailabilityStatuses({ venueIds: [venue.id] }, locale),
    {
      refetchInterval: REFETCH_VENUE_AVAILABILITY_STATUS_INTERVAL_MS,
    },
  );

  const status = useMemo(() => {
    const [currentVenue] = venuesData || [];
    return currentVenue ? getVenueAvailabilityStatus(currentVenue) : null;
  }, [venuesData]);

  return status ? (
    <AvatarBackgroundContainer>
      {status === VenueAvailabilityStatus.INACTIVE && <InactiveIcon />}
      {status !== VenueAvailabilityStatus.INACTIVE && <StatusLight status={status} />}
    </AvatarBackgroundContainer>
  ) : null;
};

export const AvailabilityStatus = () => {
  const {
    experienceEntity: { experienceEntityType, experienceEntityObject },
  } = useExperienceModuleContext();

  return experienceEntityType === ExperienceEntityType.VENUE ? (
    <VenueAvailability venue={experienceEntityObject} />
  ) : null;
};
